import { useState } from 'react'
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Layout,
  Text,
  Link,
  ProgressBar,
  Badge,
  InlineStack
} from '@shopify/polaris'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

import { shopifyApi } from '@/services/api'

export default function SetupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  
  const [shopifyData, setShopifyData] = useState({
    shopDomain: ''
  })

  const [whatsappData, setWhatsappData] = useState({
    businessId: '',
    phoneNumberId: '',
    accessToken: ''
  })

  const handleShopifySetup = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!shopifyData.shopDomain) return

    setLoading(true)
    try {
      // Call backend to initiate Shopify OAuth
      const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
      const authUrl = `${backendUrl}/api/shopify/auth/install?shop=${shopifyData.shopDomain}`

      // Redirect to backend which will redirect to Shopify OAuth
      window.location.href = authUrl
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to connect to Shopify')
    } finally {
      setLoading(false)
    }
  }

  const handleWhatsAppSetup = async (e: React.FormEvent) => {
    e.preventDefault()
    setStep(3)
  }

  const handleComplete = () => {
    toast.success('Setup completed! You can now start using WhatsApp CRM.')
    navigate('/login')
  }

  const progress = (step / 3) * 100

  return (
    <Page>
      <Layout>
        <Layout.Section>
          <div style={{ 
            maxWidth: '600px', 
            margin: '0 auto', 
            paddingTop: '60px' 
          }}>
            <Card>
              <div style={{ padding: '32px' }}>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                  <Text variant="headingLg" as="h1">
                    WhatsApp CRM Setup
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Connect your Shopify store and WhatsApp Business account
                  </Text>
                  
                  <div style={{ marginTop: '24px' }}>
                    <ProgressBar progress={progress} size="small" />
                    <Text variant="bodySm" as="p" color="subdued">
                      Step {step} of 3
                    </Text>
                  </div>
                </div>

                {step === 1 && (
                  <div>
                    <Text variant="headingMd" as="h2">
                      Connect Your Shopify Store
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Enter your Shopify store domain to get started
                    </Text>

                    <form onSubmit={handleShopifySetup} style={{ marginTop: '24px' }}>
                      <FormLayout>
                        <TextField
                          label="Shopify Store Domain"
                          value={shopifyData.shopDomain}
                          onChange={(value) => 
                            setShopifyData(prev => ({ ...prev, shopDomain: value }))
                          }
                          placeholder="your-store.myshopify.com"
                          helpText="Enter your full Shopify domain (e.g., your-store.myshopify.com)"
                          required
                        />
                        
                        <Button
                          submit
                          primary
                          fullWidth
                          loading={loading}
                          disabled={!shopifyData.shopDomain}
                        >
                          Connect to Shopify
                        </Button>
                      </FormLayout>
                    </form>

                    <div style={{ 
                      textAlign: 'center', 
                      marginTop: '24px',
                      paddingTop: '24px',
                      borderTop: '1px solid #e1e3e5'
                    }}>
                      <Text variant="bodyMd" as="p" color="subdued">
                        Already have an account?{' '}
                        <Link url="/login">Sign in here</Link>
                      </Text>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                      <Badge status="success">Shopify Connected</Badge>
                    </div>

                    <Text variant="headingMd" as="h2">
                      Connect WhatsApp Business
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Configure your WhatsApp Business API credentials
                    </Text>

                    <form onSubmit={handleWhatsAppSetup} style={{ marginTop: '24px' }}>
                      <FormLayout>
                        <TextField
                          label="Business Account ID"
                          value={whatsappData.businessId}
                          onChange={(value) => 
                            setWhatsappData(prev => ({ ...prev, businessId: value }))
                          }
                          placeholder="Enter your WhatsApp Business Account ID"
                          helpText="Found in your Facebook Business Manager"
                          required
                        />
                        
                        <TextField
                          label="Phone Number ID"
                          value={whatsappData.phoneNumberId}
                          onChange={(value) => 
                            setWhatsappData(prev => ({ ...prev, phoneNumberId: value }))
                          }
                          placeholder="Enter your WhatsApp Phone Number ID"
                          required
                        />
                        
                        <TextField
                          label="Access Token"
                          type="password"
                          value={whatsappData.accessToken}
                          onChange={(value) => 
                            setWhatsappData(prev => ({ ...prev, accessToken: value }))
                          }
                          placeholder="Enter your WhatsApp Access Token"
                          helpText="Keep this secure and never share it"
                          required
                        />
                        
                        <InlineStack gap="400">
                          <Button
                            onClick={() => setStep(1)}
                            disabled={loading}
                          >
                            Back
                          </Button>
                          <Button
                            submit
                            primary
                            loading={loading}
                            disabled={!whatsappData.businessId || !whatsappData.phoneNumberId || !whatsappData.accessToken}
                          >
                            Connect WhatsApp
                          </Button>
                        </InlineStack>
                      </FormLayout>
                    </form>

                    <div style={{ 
                      marginTop: '24px',
                      padding: '16px',
                      backgroundColor: '#f6f6f7',
                      borderRadius: '8px'
                    }}>
                      <Text variant="bodyMd" fontWeight="semibold" as="p">
                        Need help getting WhatsApp credentials?
                      </Text>
                      <Text variant="bodySm" as="p" color="subdued">
                        1. Go to Facebook Business Manager<br/>
                        2. Navigate to WhatsApp Business API<br/>
                        3. Copy your Business Account ID and Phone Number ID<br/>
                        4. Generate an access token with required permissions
                      </Text>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: '24px' }}>
                      <Badge status="success">Setup Complete!</Badge>
                    </div>

                    <Text variant="headingMd" as="h2">
                      You're All Set!
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Your WhatsApp CRM is now configured and ready to use
                    </Text>

                    <div style={{ 
                      marginTop: '32px',
                      padding: '24px',
                      backgroundColor: '#f6f6f7',
                      borderRadius: '8px',
                      textAlign: 'left'
                    }}>
                      <Text variant="bodyMd" fontWeight="semibold" as="p">
                        What's Next?
                      </Text>
                      <div style={{ marginTop: '12px' }}>
                        <Text variant="bodySm" as="p">
                          ✅ Shopify store connected<br/>
                          ✅ WhatsApp Business API configured<br/>
                          ✅ Webhooks set up for real-time updates<br/>
                          ✅ Ready to receive and send messages
                        </Text>
                      </div>
                    </div>

                    <div style={{ marginTop: '32px' }}>
                      <Button primary fullWidth onClick={handleComplete}>
                        Go to Dashboard
                      </Button>
                    </div>

                    <div style={{ 
                      textAlign: 'center', 
                      marginTop: '24px',
                      paddingTop: '24px',
                      borderTop: '1px solid #e1e3e5'
                    }}>
                      <Text variant="bodyMd" as="p" color="subdued">
                        Need help? Check out our{' '}
                        <Link url="/docs">documentation</Link>
                        {' '}or{' '}
                        <Link url="/support">contact support</Link>
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  )
}